import {
  useCreateWorkHourException,
  useUpdateWorkHourException,
  useDeleteLunchBreakForDay,
} from "../../../stores/Calendar/calendar.mutations";
import { WorkHourExceptionDto } from "../../../api-client";
import lunchBreakPromptStore from "../../../stores/LunchBreak/lunchBreakPromptStore";

/**
 * Response metadata the backend attaches to a composed work-hour-exception
 * (WorkingHoursService.computeBreakMeta). `breakOptional` is true when the saved
 * day still has a lunch break but total work fell below the statutory threshold
 * — exactly the case the backend used to silently delete. The threshold lives
 * ONLY in the backend now; the frontend just reacts to this flag.
 */
interface BreakMeta {
  breakOptional?: boolean;
  workHours?: number;
  /**
   * True only when THIS save changed the statutory minimum requirement
   * (MD-238 P6). Gates the keep/delete prompt: moving a break without
   * changing the working time must never trigger the dialog.
   */
  breakRequirementChanged?: boolean;
}

/**
 * Drop-in replacement for the raw work-hour-exception mutation hooks used by the
 * calendar controllers. Same call signatures.
 *
 * The keep/delete decision is driven ENTIRELY by the backend. After a save the
 * server returns the composed day plus `breakOptional` (true when a lunch break
 * remains on a day whose work time fell below the statutory threshold — the case
 * it used to silently delete). We just react to that flag; the threshold rule is
 * never re-implemented on the frontend.
 *
 * On "delete" we call the dedicated `deleteLunchBreakForDay` endpoint. Omitting
 * the break from a save payload does NOT delete it, because the backend
 * re-composes the break from the recurring plan.
 *
 * `promptOnOptionalBreak` gates the keep/delete dialog. It defaults to `true`
 * (the "Arbeitszeiten" timesheet view). The main calendar views pass `false`
 * so that rescheduling a break there never interrupts the user with the
 * prompt — the break is simply kept (the non-destructive choice).
 */
export function useGuardedWorkHourExceptionMutations(options?: {
  promptOnOptionalBreak?: boolean;
}) {
  const promptOnOptionalBreak = options?.promptOnOptionalBreak ?? true;
  const { mutateAsync: create } = useCreateWorkHourException();
  const { mutateAsync: update } = useUpdateWorkHourException();
  const { mutateAsync: deleteLunchBreak } = useDeleteLunchBreakForDay();

  const promptIfBreakOptional = async (
    userId: string | undefined,
    date: string | undefined,
    res: unknown
  ) => {
    if (!promptOnOptionalBreak) return;
    if (!userId || !date) return;
    // The generated client returns an AxiosResponse; fall back to the raw body
    // in case an interceptor already unwrapped `.data`.
    const meta = (((res as { data?: BreakMeta })?.data ?? res) as BreakMeta) ?? {};
    if (!meta.breakOptional) return;
    // MD-238 P6: only prompt when this save actually CHANGED the statutory
    // requirement (e.g. working time dropped below the threshold). A pure
    // break move keeps the requirement unchanged → no dialog. Backends that
    // don't send the flag fall back to the old behavior (prompt).
    if (meta.breakRequirementChanged === false) return;

    const decision = await lunchBreakPromptStore.requestDecision({
      workHours: meta.workHours ?? 0,
    });
    if (decision === "delete") {
      try {
        await deleteLunchBreak({ userId, date });
      } catch {
        /* surfaced via the mutation's onError / meta.errorMessage */
      }
    }
  };

  const createWorkHourException = async (args: { exception: WorkHourExceptionDto }) => {
    let res: unknown;
    try {
      res = await create(args);
    } catch {
      return;
    }
    await promptIfBreakOptional(args.exception.userId, args.exception.date, res);
  };

  const updateWorkHourException = async (args: {
    exceptionId: string;
    exception: WorkHourExceptionDto;
  }) => {
    let res: unknown;
    try {
      res = await update(args);
    } catch {
      return;
    }
    await promptIfBreakOptional(args.exception.userId, args.exception.date, res);
  };

  return { createWorkHourException, updateWorkHourException };
}
